import {
    SxProps,
    Theme,
    Typography,
    Box,
    Stack,
    Pagination,
    TableContainer,
    Table,
    TableRow,
    TableHead,
    TableCell,
    styled
} from '@mui/material';
import { useState, useEffect } from 'react';
import Card from '../Utils/Card';
import AncillaryHoursListing from './AncillaryHoursListing';
import { AncillaryHourModel } from '../../lib/models/AncillaryHourModel';
import useGetPaginatedData from '../../lib/hooks/use-pagination-data';
import theme from '../../theme';
import ConditionedSnackbar from '../ConditionedSnackbar';
import AddAncillaryHours from './AddAncillaryHours';
import { Oval } from 'react-loader-spinner';
import {
    useScreenBetweenSizes,
    useScreenLargerThan,
    useScreenSmallerThan
} from '../../lib/hooks/breakpoint-hooks';
import { TableIconCell } from './AncillaryHourItem';
import useTrackEvent from '../../lib/hooks/use-track-event';

export type AncillaryHoursProps = {
    currentCycleId: string;
    cycleInterval: string;
    totalAncillaryHours: string;
    refreshData: () => void;
};

const AncillaryHours = ({
    currentCycleId,
    cycleInterval,
    totalAncillaryHours,
    refreshData: refreshHeaderData
}: AncillaryHoursProps) => {
    const trackEvent = useTrackEvent();
    const { data, paginationHandler, refreshData, loading, status } =
        useGetPaginatedData<AncillaryHourModel>(`ancillary-hours/invoice-cycle/${currentCycleId}`);
    const isLargerThanSmall = useScreenLargerThan('sm');
    const isLargerThanMedium = useScreenLargerThan('md');
    const isBetweenLargeAndXLarge = useScreenBetweenSizes('lg', 'xl');
    const isSmallerThanSmall = useScreenSmallerThan('sm');
    const [showSnackbar, setShowSnackbar] = useState(false);
    const [snackbarArgument, setSnackbarArgument] = useState<string>('');
    const [errored, setErrored] = useState(false);

    useEffect(() => {
        if (!loading) {
            if (status >= 200 && status < 400 && data) {
                trackEvent('Ancillary Hours Data', {
                    status: 'success',
                    ancillaryHours: data.data.map(({ notes, activity, minutesTaken, date }) => ({
                        notes,
                        activity,
                        minutesTaken,
                        date
                    })),
                    page: data.page,
                    currentCycleId
                });
            } else {
                trackEvent('Ancillary Hours Data', {
                    status: 'failure',
                    message: 'Ancillary Hours API call response is neither 200 nor data exist'
                });
            }
        }
    }, [loading, status, data, trackEvent, currentCycleId]);

    const snackBarMessage = (action: string) => {
        switch (action) {
            case 'edit':
                return `Ancillary hour ${errored ? 'was not' : 'was'} succesfully updated.`;
            case 'delete':
                return `Ancillary hour ${errored ? 'was not' : 'was'} succesfully deleted.`;
            default:
                return '';
        }
    };

    const TableHeaderCell = styled(TableCell)({
        color: theme.palette.cardSubtitle.main,
        fontSize: 13,
        borderBottom: 'none',
        paddingRight: 0,
        paddingBottom: 0
    });

    return (
        <Stack>
            <Stack direction={'row'} justifyContent={'space-between'}>
                <Stack direction={'column'}>
                    <Typography
                        sx={{
                            fontSize: 20,
                            fontWeight: 700,
                            color: '#232A3F',
                            paddingLeft: '0.9375rem'
                        }}>
                        Ancillary Hours
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: 12,
                            fontWeight: 400,
                            color: '#979797',
                            paddingLeft: '0.9375rem',
                            paddingBottom: '0.9375rem'
                        }}>
                        {totalAncillaryHours} Total Hours
                    </Typography>
                </Stack>
                <Box>
                    <AddAncillaryHours
                        refreshHeaderData={refreshHeaderData}
                        refreshPage={refreshData}
                        cycleInterval={cycleInterval}
                    />
                </Box>
            </Stack>

            <Card sx={cardStyle(isLargerThanSmall)}>
                {!loading && data ? (
                    <Card.Body>
                        <TableContainer
                            sx={{
                                overflow: 'hidden'
                            }}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableHeaderCell>Date</TableHeaderCell>
                                        <TableHeaderCell>Activity</TableHeaderCell>
                                        <TableHeaderCell>
                                            {isBetweenLargeAndXLarge || isSmallerThanSmall
                                                ? 'Min.'
                                                : 'Minutes'}
                                        </TableHeaderCell>
                                        <TableIconCell></TableIconCell>
                                        <TableIconCell></TableIconCell>
                                        <TableIconCell></TableIconCell>
                                    </TableRow>
                                </TableHead>
                                {data && (
                                    <AncillaryHoursListing
                                        data={data.data}
                                        setSnackbarArgument={setSnackbarArgument}
                                        setShowSnackbar={setShowSnackbar}
                                        setErrored={setErrored}
                                        refreshHeaderData={refreshHeaderData}
                                        refreshPage={refreshData}
                                    />
                                )}
                            </Table>
                        </TableContainer>
                    </Card.Body>
                ) : (
                    <Box
                        sx={{ minWidth: '17.688rem', minHeight: '18.75rem' }}
                        display="flex"
                        justifyContent="center"
                        alignItems="center">
                        <Oval
                            height={60}
                            width={60}
                            color={theme.palette.primary.main}
                            wrapperStyle={{}}
                            wrapperClass=""
                            visible={true}
                            ariaLabel="oval-loading"
                            secondaryColor={theme.palette.primary.light}
                            strokeWidth={2}
                            strokeWidthSecondary={2}
                        />
                    </Box>
                )}
                {data && (
                    <Pagination
                        shape="rounded"
                        page={data.page}
                        count={data.totalPages}
                        onChange={paginationHandler}
                        sx={{
                            paddingTop: '1.25rem',
                            paddingLeft: isLargerThanMedium ? '1rem' : '0'
                        }}
                    />
                )}
            </Card>

            <ConditionedSnackbar
                open={showSnackbar}
                handleClose={() => setShowSnackbar(false)}
                severity={!errored ? 'success' : 'error'}
                message={snackBarMessage(snackbarArgument)}
            />
        </Stack>
    );
};
export default AncillaryHours;

const cardStyle: (media: boolean) => SxProps<Theme> = (media: boolean) => {
    const biggerThanSm: SxProps<Theme> = { width: '100%' };
    const smallerThanSm: SxProps<Theme> = {
        borderRadius: 0
    };

    return media ? biggerThanSm : { ...biggerThanSm, ...smallerThanSm };
};
